import { createContext, useEffect, useMemo } from "react";
// TODO: Migrar para @tanstack/react-table v8 no futuro
// Ref: MIGRATION-REACT-TABLE.md
import {
  useSortBy,
  useTable,
  useGlobalFilter,
  usePagination,
  useFilters,
  useRowSelect,
  useExpanded,
} from "react-table";

import GetIcon from "Components/Global/Icons";

import Download from "./Components/Download";
import IndeterminateCheckbox from "./Components/IndeterminateCheckbox";
import TableBody from "./Components/TableBody";
import TableFooter from "./Components/TableFooter";
import TableHead from "./Components/TableHead";
import * as S from "./styles";

export const TableContext = createContext();

const Table = ({
  data,
  columns,
  downloadColumns = columns,
  name,
  rowClick,
  download = true,
  footer = true,
  width,
  disableDownloadButton,
  headerHeight,
  selectable,
  onSelectChange,
  defaultAllSelected,
  defaultPageSize,
  defaultSelectedRowIds = [],
  stickyLastColumn = true,
  renderRowSubComponent,
}) => {
  const pageSize = defaultPageSize || 10;
  const instance = useTable(
    {
      // eslint-disable-next-line
      columns: useMemo(() => columns, []),
      data,
      initialState: { pageSize, selectedRowIds: defaultSelectedRowIds },
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    // useExpanded antes da paginação: expandir uma linha não altera o recorte
    // da página (a sub-linha acompanha a própria linha).
    useExpanded,
    usePagination,
    useRowSelect,
    (hooks) => {
      if (!selectable && !renderRowSubComponent) return;
      hooks.visibleColumns.push((columns) => [
        // Coluna do expansor (opt-in via renderRowSubComponent): botão real
        // com aria-expanded; stopPropagation para conviver com rowClick.
        ...(renderRowSubComponent
          ? [
              {
                id: "expander",
                noSearch: true,
                width: 10,
                Header: () => null,
                Cell: ({ row }) => (
                  <S.ExpandButton
                    type="button"
                    aria-expanded={row.isExpanded}
                    aria-label={
                      row.isExpanded ? "Recolher linha" : "Expandir linha"
                    }
                    onClick={(event) => {
                      event.stopPropagation();
                      row.toggleRowExpanded();
                    }}
                  >
                    <GetIcon
                      name={row.isExpanded ? "ChevronUp" : "ChevronDown"}
                      size="xs"
                    />
                  </S.ExpandButton>
                ),
              },
            ]
          : []),
        ...(selectable
          ? [
              {
                noSearch: true,
                Header: ({ getToggleAllRowsSelectedProps }) => (
                  <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
                ),
                Cell: ({ row }) => (
                  <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
                ),
                width: 10,
              },
            ]
          : []),
        ...columns,
      ]);
    },
  );

  useMemo(() => {
    if (!onSelectChange || !instance?.state?.selectedRowIds) return;
    const selectedData = [];
    for (const i of Object.keys(instance.state.selectedRowIds)) {
      selectedData.push(data.find((d) => d.id === i) || data[i]);
    }

    onSelectChange(selectedData);
    //eslint-disable-next-line
  }, [instance?.state?.selectedRowIds]);

  useEffect(() => {
    if (!selectable || !defaultAllSelected) return;

    instance.toggleAllRowsSelected();
    //eslint-disable-next-line
  }, [defaultAllSelected]);

  const selectedRows = (state) => {
    const object = state?.selectedRowIds;
    return Object.keys(object)?.length;
  };

  return (
    <TableContext.Provider value={{ ...instance }}>
      <S.Wrapper width={width}>
        <S.CustomHeader>
          {download && (
            <Download
              name={name}
              rows={data}
              downloadColumns={downloadColumns}
              disabled={disableDownloadButton}
            />
          )}
        </S.CustomHeader>
        {selectable && <>{selectedRows(instance.state)} Linhas selecionadas</>}

        <S.TableWrapper $sticky={stickyLastColumn}>
          <S.Table {...instance.getTableProps()}>
            <TableHead headerHeight={headerHeight} />
            <TableBody
              rowClick={rowClick}
              renderRowSubComponent={renderRowSubComponent}
            />
          </S.Table>
        </S.TableWrapper>
        {footer && data.length > pageSize && <TableFooter />}
      </S.Wrapper>
    </TableContext.Provider>
  );
};

export default Table;
